import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Product } from '../interfaces/products.interface';
import { ProductData } from '../../list-products/interfaces/list-products-new.interface';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private productsSubject = new BehaviorSubject<ProductData[]>([]);
  private visibleProducts = new BehaviorSubject<ProductData[]>([]);
  private allProducts: ProductData[] = [];
  private pageSize = 6; // Número de productos a cargar por lote
  private currentPage = 0;
  private loading = false;
  private allLoaded = false;
  private observer: IntersectionObserver | null = null;

  constructor() {}

  /**
   * Inicializa el servicio con la lista completa de productos
   * @param products Lista completa de productos
   */
  initProducts(products: ProductData[]): void {
    this.allProducts = [...products];
    this.currentPage = 0;
    this.allLoaded = false;
    this.productsSubject.next([]);
    this.visibleProducts.next([]);

    // Ajustar el pageSize para cargar todos los productos
    this.pageSize = this.allProducts.length;

    // Cargamos el primer lote de productos para listas pequeñas
    this.loadMoreProducts();

  }

  /**
   * Obtiene un observable con los productos visibles actualmente
   */
  getVisibleProducts(): Observable<ProductData[]> {
    return this.visibleProducts.asObservable();
  }

  /**
   * Carga más productos cuando el usuario hace scroll
   */
  loadMoreProducts(): void {
    if (this.loading || this.allLoaded) return;

    this.loading = true;
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;

    // Verificamos si hay más productos para cargar
    if (start >= this.allProducts.length) {
      this.allLoaded = true;
      this.loading = false;
      return;
    }

    // Obtenemos el siguiente lote de productos
    const nextBatch = this.allProducts.slice(start, end);

    // Actualizamos los productos visibles
    const currentProducts = this.productsSubject.value;
    const updatedProducts = [...currentProducts, ...nextBatch];
    this.productsSubject.next(updatedProducts);
    this.visibleProducts.next(updatedProducts);

    this.currentPage++;
    this.loading = false;
  }

  /**
   * Configura el observador de intersección para detectar cuando el usuario
   * llega al final de la lista y cargar más productos
   * @param element Elemento de referencia para observar (último producto o loader)
   */
  setupIntersectionObserver(element: HTMLElement): void {
    // Desconectamos el observador anterior si existe
    if (this.observer) {
      this.observer.disconnect();
    }

    // Creamos un nuevo observador
    this.observer = new IntersectionObserver((entries) => {
      // Si el elemento es visible y tenemos más productos para cargar
      if (entries[0].isIntersecting && !this.allLoaded) {
        this.loadMoreProducts();
      }
    }, { threshold: 0.1 }); // Umbral de 10% de visibilidad

    // Comenzamos a observar el elemento
    this.observer.observe(element);
  }

  /**
   * Detiene la observación cuando el componente se destruye
   */
  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * Filtra los productos según criterios específicos
   * @param filterFn Función de filtrado
   */
  filterProducts(filterFn: (product: ProductData) => boolean): void {
    const filteredProducts = this.allProducts.filter(filterFn);
    this.allProducts = filteredProducts;
    this.currentPage = 0;
    this.allLoaded = false;
    this.productsSubject.next([]);
    this.visibleProducts.next([]);
    this.loadMoreProducts();
  }
}
